import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import { CookieSettingsButton } from './CookieBanner';

const BRAND_GREEN = '#9BBF6A';
const BRAND_GREEN_DARK = '#345742';

const Footer = () => {
  const phoneDisplay = '04221-2959978';
  const phoneTel = '042212959978';
  const email = 'info@greenlionenergy.de';

  const serviceLinks = [
    { name: 'Dachsanierung', path: '/service/dachsanierung' },
    { name: 'Dachdämmung', path: '/service/innendach-daemmung' },
    { name: 'Solaranlage', path: '/service/photovoltaik-montage' },
    { name: 'Photovoltaik-Angebot', path: '/photovoltaik-angebot' },
    { name: 'PV-Rechner', path: '/photovoltaik-rechner' },
    { name: 'Dachsanierung + PV', path: '/service/dachsanierung-photovoltaik' },
    { name: 'Wärmepumpe', path: '/waermepumpe' },
    { name: 'Photovoltaik + Wärmepumpe', path: '/photovoltaik-waermepumpe' },
    { name: 'Förderservice & iSFP', path: '/service/foerderservice-isfp' },
    { name: 'Dach-Neubau', path: '/service/dach-neubau' },
    { name: 'Dachfenster', path: '/service/fensterausbau' },
  ];

  const ratgeberLinks = [
    { name: 'KfW 358 für Dachsanierungen', path: '/kfw-358-dachsanierung' },
    { name: 'Dachsanierung Kosten 2026', path: '/ratgeber/was-kostet-dachsanierung' },
    { name: 'Dachsanierung Förderung', path: '/ratgeber/dachsanierung-foerderung' },
    { name: 'Photovoltaik: Lohnt sich das?', path: '/ratgeber/photovoltaik-lohnt-sich' },
  ];

  const linkLinks = [
    { name: 'Über uns', path: '/ueber-uns' },
    { name: 'Referenzen', path: '/referenzen' },
    { name: 'Empfehlungspartner werden', path: '/partner' },
    { name: 'Dachdecker Delmenhorst', path: '/dachdecker-delmenhorst' },
    { name: 'Dachdecker Bremen', path: '/dachdecker-bremen' },
    { name: 'Dachsanierung Delmenhorst', path: '/dachsanierung-delmenhorst' },
    { name: 'Dachsanierung Bremen', path: '/dachsanierung-bremen' },
    { name: 'Dachsanierung Oldenburg', path: '/dachsanierung-oldenburg' },
    { name: 'Dachsanierung Stuhr', path: '/dachsanierung-stuhr' },
    { name: 'Dachsanierung Ganderkesee', path: '/dachsanierung-ganderkesee' },
    { name: 'Dachsanierung Weyhe', path: '/dachsanierung-weyhe' },
  ];

  return (
    <footer className="w-full">
      <div className="w-full text-white" style={{ background: 'linear-gradient(90deg, #2D4A35 0%, #3F6648 25%, #4F7A52 50%, #3F6648 75%, #2D4A35 100%)', borderTopLeftRadius: 80 }}>
        <div className="mx-auto w-full max-w-screen-2xl px-6 sm:px-8 lg:px-16 py-4 md:py-5">
          {/* ONE row: LEFT (black) + SERVICE + LINKS (aligned baseline) */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* LEFT column */}
            <div className="text-white p-4 w-full md:w-1/2">
              <p className="text-xl md:text-2xl font-black leading-tight">Green Lion Energy</p>
              <p className="mt-1 text-sm text-white/85 font-semibold">Wir bauen für Generationen</p>
              <div className="mt-3 h-1 w-16 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />

              <p className="mt-4 text-xs font-extrabold tracking-widest uppercase text-white/75">Kontakt</p>
              <div className="mt-3 space-y-2 text-sm">
                <a
                  href={`tel:${phoneTel}`}
                  className="flex items-start gap-3 text-white/90 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5" style={{ color: BRAND_GREEN }} />
                  <span>{phoneDisplay}</span>
                </a>

                <a
                  href={`mailto:${email}`}
                  className="flex items-start gap-3 text-white/90 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5" style={{ color: BRAND_GREEN }} />
                  <span className="break-all">{email}</span>
                </a>

                <div className="flex items-start gap-3 text-white/90">
                  <MapPin className="w-5 h-5 mt-0.5" style={{ color: BRAND_GREEN }} />
                  <div data-testid="footer-addresses">
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Büro &amp; Lager</p>
                    <p>Nordenhamer Straße 180</p>
                    <p>27751 Delmenhorst</p>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-white/60">Postanschrift</p>
                    <p>Hasporter Damm 31</p>
                    <p>27749 Delmenhorst</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SERVICE column */}
            <div className="w-full md:w-1/5">
              <p className="text-xs font-extrabold tracking-widest uppercase text-white/75">Service</p>
              <ul className="mt-3 space-y-2 text-sm">
                {serviceLinks.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-white/90 hover:text-white transition-colors">
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* RATGEBER column */}
            <div className="w-full md:w-1/5">
              <p className="text-xs font-extrabold tracking-widest uppercase text-white/75">Ratgeber &amp; Finanzierung</p>
              <ul className="mt-3 space-y-2 text-sm">
                {ratgeberLinks.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-white/90 hover:text-white transition-colors">
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* LINKS column */}
            <div className="w-full md:w-1/5">
              <p className="text-xs font-extrabold tracking-widest uppercase text-white/75">Links</p>
              <ul className="mt-3 space-y-2 text-sm">
                {linkLinks.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-white/90 hover:text-white transition-colors">
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom row */}
          <div className="mt-5 pt-3 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pb-20 md:pb-0">
            <p className="text-xs text-white/80">Alle Rechte vorbehalten. © 2025</p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/80 md:mr-24">
              <Link to="/impressum" className="hover:text-white transition-colors">
                Impressum
              </Link>
              <span className="text-white/25">|</span>
              <Link to="/datenschutz" className="hover:text-white transition-colors">
                Datenschutz
              </Link>
              <span className="text-white/25">|</span>
              <Link to="/agb" className="hover:text-white transition-colors">
                AGB
              </Link>
              <span className="text-white/25">|</span>
              <CookieSettingsButton />
              <span className="text-white/25">|</span>
              <Link to="/login" className="hover:text-white transition-colors text-white/60" data-testid="footer-login-link">
                Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
